import { z } from 'zod';
import { ToolCallSchema } from './tool-payloads';
/**
 * Reporting agent — SSE event schema.
 *
 * Every chunk the server sends down the SSE stream is one of these
 * discriminated-union variants. The frontend's stream consumer parses
 * each `data:` line, validates against ReportingAgentEventSchema, and
 * routes by `type`.
 *
 * See `docs/internal/features/reporting-agent-sse-protocol.md` for the
 * full wire-format spec, including framing, error semantics, and the
 * bidirectional flow for interactive cards.
 */
// ─── Tool-call lifecycle events ───────────────────────────────────────────
/**
 * Sent the moment the agent decides to call a render tool. Lets the FE
 * show a placeholder card or skeleton while the payload is computed.
 */
export const ToolCallStartedEventSchema = z.object({
    type: z.literal('tool_call_started'),
    toolCallId: z.string(),
    name: z.string(),
    /** What the step is working on, in the agent's own words: the report or lookup title. Absent for steps that have none. */
    title: z.string().optional(),
});
/**
 * Sent when the tool's payload is ready. The FE renders the actual card.
 */
export const ToolCallCompletedEventSchema = z.object({
    type: z.literal('tool_call_completed'),
    toolCallId: z.string(),
    toolCall: ToolCallSchema,
});
/**
 * Sent when a tool errors out. The FE swaps the placeholder for a
 * non-blocking error card; the agent loop may continue.
 */
export const ToolCallFailedEventSchema = z.object({
    type: z.literal('tool_call_failed'),
    toolCallId: z.string(),
    name: z.string(),
    error: z.string(),
});
/**
 * Sent when the agent has read data it is not putting on screen: a look at
 * the rows before it presents. Carries the compact view the model was given,
 * so a client can say what was consulted and a figure in the prose can be
 * traced to its source. Clients that do not know this event drop it.
 */
export const LookupCompletedEventSchema = z.object({
    type: z.literal('lookup_completed'),
    toolCallId: z.string(),
    title: z.string().optional(),
    rowCount: z.number().optional(),
    result: z.unknown(),
});
// ─── Plain text streaming ─────────────────────────────────────────────────
/**
 * Streamed text the agent generates between tool calls (e.g. an
 * explanation of why a metric was picked). Concatenate deltas in order.
 */
export const TextDeltaEventSchema = z.object({
    type: z.literal('text_delta'),
    text: z.string(),
});
// ─── Stream-level signals ─────────────────────────────────────────────────
/**
 * Terminal failure — nothing more is coming. Distinct from
 * tool_call_failed which is recoverable.
 */
export const ErrorEventSchema = z.object({
    type: z.literal('error'),
    message: z.string(),
    code: z.string().optional(),
});
/**
 * End of stream. Always the last event. Carries the persisted thread
 * + message ids so the FE can reconcile state.
 */
export const DoneEventSchema = z.object({
    type: z.literal('done'),
    threadId: z.string(),
    messageId: z.string(),
});
// ─── Discriminated union (the wire format) ────────────────────────────────
export const ReportingAgentEventSchema = z.discriminatedUnion('type', [
    ToolCallStartedEventSchema,
    ToolCallCompletedEventSchema,
    ToolCallFailedEventSchema,
    LookupCompletedEventSchema,
    TextDeltaEventSchema,
    ErrorEventSchema,
    DoneEventSchema,
]);
//# sourceMappingURL=sse-events.js.map